import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../axios/api.js';

// Thunks backing the `company/:id` route (CompanyDetails) and its child dialogs.

const asMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// ---------------------------------------------------------------- company

export const fetchCompany = createAsyncThunk(
  'company/fetchCompany',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/company/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching company details. Please try again later.'));
    }
  }
);

export const fetchRfqReceived = createAsyncThunk(
  'company/fetchRfqReceived',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/company-inquiry/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch RFQ received count'));
    }
  }
);

export const fetchRfqQuoted = createAsyncThunk(
  'company/fetchRfqQuoted',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/company-quotation-count/${id}`);
      return response.data.rfq_quoted;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch RFQ quoted count'));
    }
  }
);

export const updateCompanyOverview = createAsyncThunk(
  'company/updateCompanyOverview',
  async ({ company, id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/companyoverview/', { company, id });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Overview. Please try again.'));
    }
  }
);

export const updateCompanyEmail = createAsyncThunk(
  'company/updateCompanyEmail',
  async ({ company_email, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/currentcompanyemail/', { company_email, id });
      dispatch(fetchCompany(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Email. Please try again.'));
    }
  }
);

// ------------------------------------------------------------------ cards

export const fetchCards = createAsyncThunk(
  'company/fetchCards',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/cards', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].company_type) {
        return response.data[0].company_type;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching cards'));
    }
  }
);

export const fetchCardsProduct = createAsyncThunk(
  'company/fetchCardsProduct',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/cardsProduct', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].product) {
        return response.data[0].product;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching product cards'));
    }
  }
);

export const fetchCardsCategory = createAsyncThunk(
  'company/fetchCardsCategory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/cardsCategory', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].client_category) {
        return response.data[0].client_category;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching category cards'));
    }
  }
);

export const fetchCardsHandledBy = createAsyncThunk(
  'company/fetchCardsHandledBy',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/cardsHandledBy', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].assigned) {
        return response.data[0].assigned;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching handled-by cards'));
    }
  }
);

export const fetchIndustryDetails = createAsyncThunk(
  'company/fetchIndustryDetails',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/details', { params: { id } });
      if (response.status === 200 && response.data.length > 0 && response.data[0].company_industry !== null) {
        return response.data[0].company_industry;
      }
      return [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching details'));
    }
  }
);

export const addCompanyType = createAsyncThunk(
  'company/addCompanyType',
  async ({ company_type, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companytypeadd/', { company_type, id });
      dispatch(fetchCards(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to add Company Type. Please try again.'));
    }
  }
);

export const editCompanyType = createAsyncThunk(
  'company/editCompanyType',
  async ({ company_type, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/companytypeedit/', { company_type, id });
      dispatch(fetchCards(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Type. Please try again.'));
    }
  }
);

export const addCompanyProduct = createAsyncThunk(
  'company/addCompanyProduct',
  async ({ product, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companyproductadd/', { product, id });
      dispatch(fetchCardsProduct(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to add Company Product. Please try again.'));
    }
  }
);

export const editCompanyProduct = createAsyncThunk(
  'company/editCompanyProduct',
  async ({ product, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/companyproductedit/', { product, id });
      dispatch(fetchCardsProduct(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Product. Please try again.'));
    }
  }
);

export const addCompanyCategory = createAsyncThunk(
  'company/addCompanyCategory',
  async ({ category, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companycategoryadd/', { category, id });
      dispatch(fetchCardsCategory(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to add Client Category. Please try again.'));
    }
  }
);

export const editCompanyCategory = createAsyncThunk(
  'company/editCompanyCategory',
  async ({ category, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/companycategoryedit/', { category, id });
      dispatch(fetchCardsCategory(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Client Category. Please try again.'));
    }
  }
);

export const addCompanyHandledBy = createAsyncThunk(
  'company/addCompanyHandledBy',
  async ({ handledBy, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companyhandledbyadd/', { handledBy, id });
      dispatch(fetchCardsHandledBy(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to add Assigned list. Please try again.'));
    }
  }
);

export const editCompanyHandledBy = createAsyncThunk(
  'company/editCompanyHandledBy',
  async ({ handledBy, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companyhadledbyedit/', { handledBy, id });
      dispatch(fetchCardsHandledBy(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Assigned list. Please try again.'));
    }
  }
);

export const addIndustry = createAsyncThunk(
  'company/addIndustry',
  async ({ industry, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/industryadd/', { industry, id });
      dispatch(fetchIndustryDetails(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to Add Industry. Please try again.'));
    }
  }
);

export const editIndustry = createAsyncThunk(
  'company/editIndustry',
  async ({ industry, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/industryedit/', { industry, id });
      dispatch(fetchIndustryDetails(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Industry. Please try again.'));
    }
  }
);

// --------------------------------------------------------------- contacts

export const fetchAllContacts = createAsyncThunk(
  'company/fetchAllContacts',
  async ({ id, limit, offset, search, filterMode }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/allcompanydata/${id}`, {
        params: { limit, offset, search, filterMode },
      });
      if (response.data && response.data.data) {
        return { data: response.data.data, total: response.data.total };
      }
      return { data: response.data, total: response.data.length };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch contacts'));
    }
  }
);

export const addAllContact = createAsyncThunk(
  'company/addAllContact',
  async ({ companyallcontacts, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/addcompanyallcontacts/${companyallcontacts}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Adding Contact error'));
    }
  }
);

export const fetchContactToEdit = createAsyncThunk(
  'company/fetchContactToEdit',
  async (currentallcontacts_id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/toeditallcompanydata/${currentallcontacts_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch contact'));
    }
  }
);

export const updateAllContact = createAsyncThunk(
  'company/updateAllContact',
  async ({ currentallcontactsid, editedContacts }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/allcontacts/${currentallcontactsid}`, {
        editedContacts,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit contact error'));
    }
  }
);

// Rejects with `{ conflict: true, ...usage }` on 409 so the caller can open the
// reassign dialog instead of showing a generic failure.
export const deleteAllContact = createAsyncThunk(
  'company/deleteAllContact',
  async (contactId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/allcontacts/${contactId}`);
      return contactId;
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue({ conflict: true, ...error.response.data, contactId });
      }
      return rejectWithValue({ message: asMessage(error, 'Delete Contact error') });
    }
  }
);

export const toggleContactSubscription = createAsyncThunk(
  'company/toggleContactSubscription',
  async ({ contactId, subscribed }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/currentallcontactsdata/${contactId}/subscribed`, {
        subscribed,
      });
      return { contactId, subscribed, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update subscription'));
    }
  }
);

export const setContactsArchived = createAsyncThunk(
  'company/setContactsArchived',
  async ({ ids, archived }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/currentallcontactsdata/archive-status', { ids, archived });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to move contacts'));
    }
  }
);

export const reassignAndDeleteContact = createAsyncThunk(
  'company/reassignAndDeleteContact',
  async (body, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/contact-reassign-and-delete', body);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to reassign and delete contact'));
    }
  }
);

export const uploadAllContacts = createAsyncThunk(
  'company/uploadAllContacts',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/allcontactsupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

// -------------------------------------------------------- shipping/billing

export const fetchShippingData = createAsyncThunk(
  'company/fetchShippingData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/currentshippingdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch shipping addresses'));
    }
  }
);

export const fetchBillingData = createAsyncThunk(
  'company/fetchBillingData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/currentbillingdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch billing addresses'));
    }
  }
);

export const addShippingAddress = createAsyncThunk(
  'company/addShippingAddress',
  async ({ companynameshippingaddress, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/currentcompanyshippingaddress/${companynameshippingaddress}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Shipping address add error'));
    }
  }
);

export const addBillingAddress = createAsyncThunk(
  'company/addBillingAddress',
  async ({ companynamesbillingaddress, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/currentcompanybillingaddress/${companynamesbillingaddress}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Billing address add error'));
    }
  }
);

export const fetchShippingToEdit = createAsyncThunk(
  'company/fetchShippingToEdit',
  async (shippingid, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/editcurrentshippingdata/${shippingid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch shipping address'));
    }
  }
);

export const fetchBillingToEdit = createAsyncThunk(
  'company/fetchBillingToEdit',
  async (billingid, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/editcurrentbillingdata/${billingid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch billing address'));
    }
  }
);

export const editShippingAddress = createAsyncThunk(
  'company/editShippingAddress',
  async ({ editshippingaddressdata, shippingid }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/shippingedit', {
        editshippingaddressdata,
        shippingid,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Shipping address edit error'));
    }
  }
);

export const editBillingAddress = createAsyncThunk(
  'company/editBillingAddress',
  async ({ editbillingaddressdata, billingid }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/billingaddressedit', {
        editbillingaddressdata,
        billingid,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Billing address edit error'));
    }
  }
);

export const deleteShippingAddress = createAsyncThunk(
  'company/deleteShippingAddress',
  async (shippingid, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/companyshippingaddress/${shippingid}`);
      return shippingid;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete shipping address error'));
    }
  }
);

export const deleteBillingAddress = createAsyncThunk(
  'company/deleteBillingAddress',
  async (billingid, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/companybillingaddress/${billingid}`);
      return billingid;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete billing address error'));
    }
  }
);

export const uploadShippingAddresses = createAsyncThunk(
  'company/uploadShippingAddresses',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/shippingupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const uploadBillingAddresses = createAsyncThunk(
  'company/uploadBillingAddresses',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/billingupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

// ----------------------------------------------------------- certificates

export const fetchCertificates = createAsyncThunk(
  'company/fetchCertificates',
  async ({ id, limit, offset, search, valueExpired, valueDeleted, showArchived }, { rejectWithValue }) => {
    const params = { limit, offset, search };
    try {
      let response;
      if (valueExpired) {
        response = await axiosClient.post('/api/currentcertificatesfilterdata/', {
          id,
          status: 'archived',
          ...params,
        });
      } else if (valueDeleted) {
        response = await axiosClient.post('/api/currentcertificatesfilterdata/', {
          id,
          status: 'deleted',
          ...params,
        });
      } else if (showArchived) {
        response = await axiosClient.get(`/api/currentcertificatesarchivedata/${id}`, { params });
      } else {
        response = await axiosClient.get(`/api/currentcertificatesdata/${id}`, { params });
      }

      if (response.data && response.data.data) {
        return { data: response.data.data, total: response.data.total };
      }
      return { data: response.data, total: response.data.length };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch certificates'));
    }
  }
);

export const uploadCertificate = createAsyncThunk(
  'company/uploadCertificate',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/current-certificate-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const fetchCertificateToEdit = createAsyncThunk(
  'company/fetchCertificateToEdit',
  async (certificateId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/toeditcurrentcertificatesdata/${certificateId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch certificate'));
    }
  }
);

export const editCertificate = createAsyncThunk(
  'company/editCertificate',
  async ({ certificateId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/currenteditcertificates/${certificateId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit certificate error'));
    }
  }
);

export const deleteCertificate = createAsyncThunk(
  'company/deleteCertificate',
  async (certificateId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/currentcertificates/${certificateId}`);
      return certificateId;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete certificate error'));
    }
  }
);

// ------------------------------------------------------------------ leads

export const fetchLeads = createAsyncThunk(
  'company/fetchLeads',
  async ({ id, limit, offset, search }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/companydetailsrfq', {
        id,
        limit,
        offset,
        search,
      });
      if (response.data && response.data.data) {
        return { data: response.data.data, total: response.data.total };
      }
      return { data: response.data, total: response.data.length };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching leads'));
    }
  }
);

export const deleteLead = createAsyncThunk(
  'company/deleteLead',
  async (leadId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/leads/delete/${leadId}`);
      return leadId;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error occurred while deleting the lead'));
    }
  }
);

export const fetchInquiryDetails = createAsyncThunk(
  'company/fetchInquiryDetails',
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/inquiry-details', {
        params: { rfq_no },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load lead data'));
    }
  }
);

export const submitLeadUpdate = createAsyncThunk(
  'company/submitLeadUpdate',
  async ({ rfq_no, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/lead/${rfq_no}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update lead'));
    }
  }
);

export const fetchClientNames = createAsyncThunk(
  'company/fetchClientNames',
  async (market, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/clientsname', { params: { market } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch client names'));
    }
  }
);

// ---------------------------------------------------------------- vendors

export const fetchAssignedVendors = createAsyncThunk(
  'company/fetchAssignedVendors',
  async ({ id, limit, offset, search }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/company/${id}/vendor-assigned`, {
        params: { limit, offset, search },
      });
      return { data: response.data.data, total: response.data.total };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching assigned vendors'));
    }
  }
);

export const fetchVendorOptions = createAsyncThunk(
  'company/fetchVendorOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/vendor-trial-names');
      return (response.data || []).map((v) => v.vendor_name);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching vendor options'));
    }
  }
);

export const saveAssignedVendors = createAsyncThunk(
  'company/saveAssignedVendors',
  async ({ id, vendor_assigned }, { rejectWithValue }) => {
    try {
      await axiosClient.put(`/api/company/${id}/vendor-assigned`, { vendor_assigned });
      return vendor_assigned;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to save vendors'));
    }
  }
);

// ------------------------------------------------------------- reference

export const fetchCountryCodeOptions = createAsyncThunk(
  'company/fetchCountryCodeOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/allcompanycountrydata/');
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch country codes'));
    }
  }
);

export const fetchCountryDialCode = createAsyncThunk(
  'company/fetchCountryDialCode',
  async (country, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/get-country-code?country=${encodeURIComponent(country)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch country dial code'));
    }
  }
);

export const fetchLocationData = createAsyncThunk(
  'company/fetchLocationData',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/locationdata/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching location data'));
    }
  }
);

const TEMPLATE_ENDPOINTS = {
  allContacts: '/fetch-allcontacts-documentformat',
  address: '/fetch-address-document-format',
  clients: '/fetch-clients-documentformat',
};

// Download links for the bulk-upload spreadsheet templates.
export const fetchTemplateUrl = createAsyncThunk(
  'company/fetchTemplateUrl',
  async (kind, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(TEMPLATE_ENDPOINTS[kind]);
      return { kind, url: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch template URL'));
    }
  }
);

// Falls back to the material-code list when the product list endpoint fails,
// mirroring the behaviour the page had inline.
export const fetchProductOptions = createAsyncThunk(
  'company/fetchProductOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/product-list');
      return response.data;
    } catch (error) {
      try {
        const fallback = await axiosClient.get('/api/material-code/material_code_product');
        return fallback.data;
      } catch (fallbackError) {
        return rejectWithValue(asMessage(fallbackError, 'Failed to fetch product list'));
      }
    }
  }
);

const initialState = {
  // Company record
  company: {},
  companyLoading: false,
  companyError: null,

  rfqReceived: 0,
  rfqQuoted: 0,
  orderReceived: 0,
  orderWon: 0,
  orderLost: 0,

  // Cards
  cards: [],
  cardsProduct: [],
  cardsCategory: [],
  cardsHandledBy: [],
  industryDetails: [],
  cardsLoading: false,

  // Contacts
  allContacts: [],
  allContactsTotal: 0,
  allContactsLoading: false,
  allContactsError: null,
  contactToEdit: [],
  // Populated when a delete is refused with 409 so the page can open the
  // reassign dialog.
  reassignUsageData: null,

  // Addresses
  shippingAddressData: [],
  billingAddressData: [],
  shippingToEdit: [],
  billingToEdit: [],
  addressLoading: false,

  // Certificates
  certificates: [],
  certificatesTotal: 0,
  certificatesLoading: false,
  certificateToEdit: [],

  // Leads
  leads: [],
  leadsTotal: 0,
  leadsLoading: false,
  clientNames: [],

  // Vendors
  assignedVendors: [],
  assignedVendorsTotal: 0,
  vendorOptions: [],
  assignedVendorsLoading: false,

  // Reference data
  countryCodeOptions: [],
  locationOptions: [],
  productOptions: [],
  templateUrls: { allContacts: '', address: '', clients: '' },

  // Shared page-level flags
  pageLoading: false,
  saving: false,
  error: null,
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setCompanyField: (state, action) => {
      const { name, value } = action.payload;
      state.company = { ...state.company, [name]: value };
    },
    setCompany: (state, action) => {
      state.company = action.payload || {};
    },
    setReassignUsageData: (state, action) => {
      state.reassignUsageData = action.payload;
    },
    clearReassignUsageData: (state) => {
      state.reassignUsageData = null;
    },
    setContactToEdit: (state, action) => {
      state.contactToEdit = action.payload;
    },
    // Lets the inline table editors mutate the fetched rows before saving.
    setAllContacts: (state, action) => {
      state.allContacts = action.payload;
    },
    setCertificates: (state, action) => {
      state.certificates = action.payload;
    },
    setCertificateToEdit: (state, action) => {
      state.certificateToEdit = action.payload;
    },
    setShippingToEdit: (state, action) => {
      state.shippingToEdit = action.payload;
    },
    setBillingToEdit: (state, action) => {
      state.billingToEdit = action.payload;
    },
    setLocationOptions: (state, action) => {
      state.locationOptions = action.payload;
    },
    setPageLoading: (state, action) => {
      state.pageLoading = action.payload;
    },
    clearCompanyError: (state) => {
      state.error = null;
      state.companyError = null;
      state.allContactsError = null;
    },
    resetCompanyState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Company
      .addCase(fetchCompany.pending, (state) => {
        state.companyLoading = true;
        state.companyError = null;
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.companyLoading = false;
        state.company = action.payload;
      })
      .addCase(fetchCompany.rejected, (state, action) => {
        state.companyLoading = false;
        state.companyError = action.payload;
        state.company = {};
      })
      .addCase(fetchRfqReceived.fulfilled, (state, action) => {
        state.rfqReceived = action.payload.rfq_received;
        state.orderReceived = Number(action.payload.order_received) || 0;
        state.orderWon = Number(action.payload.order_won) || 0;
        state.orderLost = Number(action.payload.order_lost) || 0;
      })
      .addCase(fetchRfqQuoted.fulfilled, (state, action) => {
        state.rfqQuoted = action.payload;
      })

      // Cards
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.cards = action.payload;
      })
      .addCase(fetchCards.rejected, (state) => {
        state.cards = [];
      })
      .addCase(fetchCardsProduct.fulfilled, (state, action) => {
        state.cardsProduct = action.payload;
      })
      .addCase(fetchCardsProduct.rejected, (state) => {
        state.cardsProduct = [];
      })
      .addCase(fetchCardsCategory.fulfilled, (state, action) => {
        state.cardsCategory = action.payload;
      })
      .addCase(fetchCardsCategory.rejected, (state) => {
        state.cardsCategory = [];
      })
      .addCase(fetchCardsHandledBy.fulfilled, (state, action) => {
        state.cardsHandledBy = action.payload;
      })
      .addCase(fetchCardsHandledBy.rejected, (state) => {
        state.cardsHandledBy = [];
      })
      .addCase(fetchIndustryDetails.fulfilled, (state, action) => {
        state.industryDetails = action.payload;
      })
      .addCase(fetchIndustryDetails.rejected, (state) => {
        state.industryDetails = [];
      })

      // Contacts
      .addCase(fetchAllContacts.pending, (state) => {
        state.allContactsLoading = true;
        state.allContactsError = null;
      })
      .addCase(fetchAllContacts.fulfilled, (state, action) => {
        state.allContactsLoading = false;
        state.allContacts = action.payload.data;
        state.allContactsTotal = action.payload.total;
      })
      .addCase(fetchAllContacts.rejected, (state, action) => {
        state.allContactsLoading = false;
        state.allContactsError = action.payload;
      })
      .addCase(fetchContactToEdit.fulfilled, (state, action) => {
        state.contactToEdit = action.payload;
      })
      .addCase(deleteAllContact.rejected, (state, action) => {
        if (action.payload?.conflict) {
          state.reassignUsageData = action.payload;
        } else {
          state.allContactsError = action.payload?.message;
        }
      })

      // Addresses
      .addCase(fetchShippingData.fulfilled, (state, action) => {
        state.shippingAddressData = action.payload;
      })
      .addCase(fetchBillingData.fulfilled, (state, action) => {
        state.billingAddressData = action.payload;
      })
      .addCase(fetchShippingToEdit.fulfilled, (state, action) => {
        state.shippingToEdit = action.payload;
      })
      .addCase(fetchBillingToEdit.fulfilled, (state, action) => {
        state.billingToEdit = action.payload;
      })

      // Certificates
      .addCase(fetchCertificates.pending, (state) => {
        state.certificatesLoading = true;
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.certificatesLoading = false;
        state.certificates = action.payload.data;
        state.certificatesTotal = action.payload.total;
      })
      .addCase(fetchCertificates.rejected, (state, action) => {
        state.certificatesLoading = false;
        state.certificates = [];
        state.certificatesTotal = 0;
        state.error = action.payload;
      })
      .addCase(fetchCertificateToEdit.fulfilled, (state, action) => {
        state.certificateToEdit = action.payload;
      })

      // Leads
      .addCase(fetchLeads.pending, (state) => {
        state.leadsLoading = true;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload.data;
        state.leadsTotal = action.payload.total;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.leadsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchClientNames.fulfilled, (state, action) => {
        state.clientNames = action.payload;
      })

      // Vendors
      .addCase(fetchAssignedVendors.pending, (state) => {
        state.assignedVendorsLoading = true;
      })
      .addCase(fetchAssignedVendors.fulfilled, (state, action) => {
        state.assignedVendorsLoading = false;
        state.assignedVendors = action.payload.data;
        state.assignedVendorsTotal = action.payload.total;
      })
      .addCase(fetchAssignedVendors.rejected, (state, action) => {
        state.assignedVendorsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchVendorOptions.fulfilled, (state, action) => {
        state.vendorOptions = action.payload;
      })
      .addCase(saveAssignedVendors.fulfilled, (state, action) => {
        state.company = {
          ...state.company,
          vendor_assigned: JSON.stringify(action.payload),
        };
      })

      // Reference data
      .addCase(fetchCountryCodeOptions.fulfilled, (state, action) => {
        state.countryCodeOptions = action.payload;
      })
      .addCase(fetchProductOptions.fulfilled, (state, action) => {
        state.productOptions = action.payload;
      })
      .addCase(fetchTemplateUrl.fulfilled, (state, action) => {
        state.templateUrls[action.payload.kind] = action.payload.url;
      });
  },
});

export const {
  setCompanyField,
  setCompany,
  setReassignUsageData,
  clearReassignUsageData,
  setContactToEdit,
  setAllContacts,
  setCertificates,
  setCertificateToEdit,
  setShippingToEdit,
  setBillingToEdit,
  setLocationOptions,
  setPageLoading,
  clearCompanyError,
  resetCompanyState,
} = companySlice.actions;

export default companySlice.reducer;
